// TOEFL iBT Mock 9 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2. Unlike Mocks 2–7,
// `upper` and `lower` are different sets, so the adaptive routing is real.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
  {
    id: "m1-words",
    type: "words",
    title: "Ocean Tides",
    text: `Tides are the regular rise and fall of the sea along the coast. They [[are]] caused [[mainly]] by [[the]] pull [[of]] the Moon's [[gravity]] [[on]] Earth's [[oceans]]. Because [[Earth]] rotates, [[most]] coastlines [[experience]] two [[high]] [[tides]] [[and]] two low tides each day. The Sun also affects tides, and when the Sun and Moon line up, the tides become especially strong.`,
  },
  {
    id: "m1-daily1",
    type: "daily",
    title: "Dining Services Survey",
    text: `Dining Services wants your feedback! From November 3 to 14, all students with a meal plan can complete a ten-minute online survey about food quality, menu variety, and opening hours. Everyone who finishes the survey will receive a coupon for a free smoothie at the Campus Café. In addition, five participants will be selected at random to win a $100 credit on their meal card. Results will be shared at the Student Senate meeting in December, and Dining Services will announce any changes for the spring semester at that time.`,
    questions: [
      {
        question: "Who can take the survey?",
        options: [
          "Only Student Senate members",
          "Students with a meal plan",
          "Café employees",
          "All campus visitors",
        ],
        correct: 1,
      },
      {
        question: "What will every participant receive?",
        options: [
          "A $100 meal card credit",
          "A free lunch",
          "A coupon for a free smoothie",
          "A copy of the spring menu",
        ],
        correct: 2,
      },
      {
        question: "What can be inferred about the menu for the spring semester?",
        options: [
          "It may be changed based on student responses.",
          "It will be the same as this semester's menu.",
          "It was chosen by the Student Senate.",
          "It will be announced on November 14.",
        ],
        correct: 0,
      },
    ],
  },
  {
    id: "m1-daily2",
    type: "daily",
    title: "Internship Offer Email",
    text: `Subject: Summer internship offer

Dear Ms. Nakamura,

On behalf of Brightwave Media, I am pleased to offer you a position as a marketing intern this summer. The internship runs for ten weeks, from June 2 to August 8, and pays $18 per hour for up to 30 hours per week. You will work mainly with our social media team, although you will also have the opportunity to assist with two client presentations.

To accept this offer, please sign the attached agreement and return it by April 15. If we do not hear from you by that date, we may offer the position to another candidate. Please also let us know whether you will need help finding short-term housing in the city.

Sincerely,
Daniel Owusu
Human Resources Manager`,
    questions: [
      {
        question: "What is the main purpose of the email?",
        options: [
          "To schedule a job interview",
          "To request help with a presentation",
          "To explain a change in pay",
          "To offer an internship position",
        ],
        correct: 3,
      },
      {
        question: "What will Ms. Nakamura mainly do during the internship?",
        options: [
          "Find housing for other interns",
          "Work with the social media team",
          "Manage client accounts",
          "Hire new employees",
        ],
        correct: 1,
      },
      {
        question: "What might happen if Ms. Nakamura does not reply by April 15?",
        options: [
          "Her pay will be reduced.",
          "The internship will be shortened.",
          "Someone else may be offered the position.",
          "She will have to find her own housing.",
        ],
        correct: 2,
      },
    ],
  },
  {
    id: "m1-academic",
    type: "academic",
    title: "The Origins of Writing in Mesopotamia",
    text: `Writing appears to have been invented independently in several parts of the world, but the earliest known system developed in Mesopotamia, in what is now southern Iraq, around 3200 BCE. Surprisingly, it did not begin as a way to record stories, laws, or religious texts. Its origins were thoroughly practical: it emerged to keep track of goods.

For thousands of years before writing, people in the region used small clay tokens of different shapes to represent quantities of goods such as grain, oil, and livestock. [A] To secure a transaction, tokens were sometimes sealed inside hollow clay balls. Because the contents could not be seen without breaking the ball, people began pressing the tokens into its soft surface before sealing it, leaving marks that showed what was inside. [B] Eventually, scribes realized that the marks alone were sufficient, and the tokens themselves became unnecessary.

Over the following centuries, these impressions evolved into a script known as cuneiform, from the Latin word for "wedge," because scribes pressed a cut reed into wet clay to make wedge-shaped marks. [C] Early signs were largely pictures of objects, but gradually many came to represent sounds, allowing scribes to write names, verbs, and eventually complete sentences.

This shift transformed writing from an accounting tool into a means of preserving knowledge. [D] Within a few centuries, Mesopotamian scribes were recording laws, letters, medical remedies, and literature, including the Epic of Gilgamesh, one of the oldest surviving works of fiction.`,
    questions: [
      {
        question: "The word \"sufficient\" in the passage is closest in meaning to",
        options: ["enough", "confusing", "permanent", "decorative"],
        correct: 0,
      },
      {
        question: "According to paragraph 1, why was writing first developed in Mesopotamia?",
        options: [
          "To record religious ceremonies",
          "To keep records of goods",
          "To preserve stories",
          "To communicate with distant cities",
        ],
        correct: 1,
      },
      {
        question: "According to paragraph 2, why did people press tokens into the surface of the clay balls?",
        options: [
          "To make the balls stronger",
          "To decorate them for ceremonies",
          "To count how many balls there were",
          "To show what was inside without breaking them",
        ],
        correct: 3,
      },
      {
        question: "What can be inferred from paragraph 3 about signs that represented sounds?",
        options: [
          "They were used only for numbers.",
          "They replaced the use of clay.",
          "They made it possible to write more than lists of objects.",
          "They were invented before clay tokens.",
        ],
        correct: 2,
      },
      {
        question: "Where would the following sentence best fit? \"Archaeologists have found thousands of these tokens at sites across the Middle East.\"",
        options: ["[A]", "[B]", "[C]", "[D]"],
        correct: 0,
      },
    ],
  },
];

// ── Harder Module 2 ──────────────────────────────────────────
const upper = [
  {
    id: "up-words",
    type: "words",
    title: "Antibiotic Resistance",
    text: `Antibiotics are medicines that kill bacteria or stop them from multiplying. However, [[their]] widespread [[use]] has [[encouraged]] the [[evolution]] of [[resistant]] strains. [[When]] a [[population]] of [[bacteria]] is [[exposed]] to a drug, [[the]] few [[individuals]] with [[protective]] mutations survive and reproduce. Over time, these resistant bacteria can come to dominate, making once-treatable infections dangerous again.`,
  },
  {
    id: "up-daily1",
    type: "daily",
    title: "Symposium Registration Confirmation",
    text: `Registration Confirmation — Northeast Undergraduate Research Symposium

Thank you for registering. Your poster, "Microplastics in Urban Streams," has been assigned to Session 3 (Saturday, 1:30–3:00 p.m.) in Hall B. Posters must be mounted between 12:30 and 1:15 p.m.; boards and push pins will be provided, but presenters are responsible for printing their own posters, which may not exceed 36 by 48 inches.

Your registration fee includes lunch on Saturday and admission to the keynote address on Friday evening. Please note that the fee is non-refundable after March 1; however, registrations may be transferred to another student from your institution by emailing the organizing committee.`,
    questions: [
      {
        question: "What must the presenter bring?",
        options: ["Push pins", "A printed poster", "A display board", "A lunch ticket"],
        correct: 1,
      },
      {
        question: "What can be inferred about a presenter who arrives at 1:20 p.m. on Saturday?",
        options: [
          "They will be too late to mount their poster in the stated time.",
          "They will miss the keynote address.",
          "They will not receive lunch.",
          "They will be moved to Session 4.",
        ],
        correct: 0,
      },
      {
        question: "What option is available to a registered student who cannot attend after March 1?",
        options: [
          "Requesting a full refund",
          "Presenting on Friday instead",
          "Transferring the registration to another student from the same school",
          "Mailing the poster to the committee",
        ],
        correct: 2,
      },
    ],
  },
  {
    id: "up-daily2",
    type: "daily",
    title: "Office Relocation Memo",
    text: `MEMO
To: All Staff, Department of Economics
From: Ruth Abara, Department Administrator
Re: Temporary relocation

Due to the installation of a new ventilation system, faculty offices on the fourth floor of Carlton Hall will be unavailable from May 19 through June 27. Affected faculty will be temporarily relocated to the second floor of the Wexler Library annex. Movers will transport boxed materials on May 16; any items not packed and labeled by noon that day will remain in place and may be inaccessible for the duration of the work.

Phone extensions will be forwarded automatically, but mail will not be redirected; please collect it from the main department office on the first floor of Carlton Hall.`,
    questions: [
      {
        question: "What is the main purpose of the memo?",
        options: [
          "To inform staff about a temporary office move",
          "To announce new ventilation rules",
          "To ask faculty to change their phone extensions",
          "To announce that the library annex will close",
        ],
        correct: 0,
      },
      {
        question: "The word \"inaccessible\" in the memo is closest in meaning to",
        options: ["damaged", "thrown away", "heavy", "impossible to reach"],
        correct: 3,
      },
      {
        question: "Where should faculty pick up their mail during the relocation?",
        options: [
          "At the Wexler Library annex",
          "On the fourth floor of Carlton Hall",
          "At the main department office in Carlton Hall",
          "It will be forwarded to their homes.",
        ],
        correct: 2,
      },
    ],
  },
  {
    id: "up-academic",
    type: "academic",
    title: "Island Gigantism and Dwarfism",
    text: `Isolated islands have long fascinated biologists because their inhabitants often evolve in unusual directions. One of the most striking patterns is that animal body size tends to change after a species colonizes an island. Large mammals frequently become smaller over many generations, a process called insular dwarfism, while small animals such as rodents and lizards often become larger, a process called insular gigantism.

Dwarfism in large animals is usually explained by limited resources. [A] An island offers far less food than a continent, and a population of large-bodied animals may not survive if each individual requires a great deal of it. Smaller individuals need less food and can reproduce on fewer resources, so over time natural selection favors them. The fossil record contains dramatic examples: dwarf elephants no taller than a large dog once lived on several Mediterranean islands.

Gigantism in small animals has a different explanation. On continents, small mammals face numerous predators, and small size helps them hide and escape. Many islands, however, lack large predators altogether. [B] Freed from this pressure, small animals can benefit from the advantages of larger size, such as better heat retention and the ability to compete for a wider range of food. [C] The giant rats of some Indonesian islands, several times the size of their mainland relatives, illustrate this trend.

Researchers caution, however, that the so-called island rule is a tendency rather than a law. [D] The direction and degree of change depend on many factors, including the size of the island, how long the species has been isolated, and whether competitors are present. Some studies suggest that once these variables are taken into account, the pattern is considerably weaker than early researchers believed.`,
    questions: [
      {
        question: "The word \"striking\" in the passage is closest in meaning to",
        options: ["violent", "noticeable", "recent", "temporary"],
        correct: 1,
      },
      {
        question: "According to paragraph 2, why do large animals tend to become smaller on islands?",
        options: [
          "Islands have more predators.",
          "Large animals cannot tolerate island climates.",
          "Islands provide less food to support them.",
          "Smaller animals can swim to nearby islands.",
        ],
        correct: 2,
      },
      {
        question: "What can be inferred from paragraph 3 about small mammals on continents?",
        options: [
          "Their small size is partly a response to predators.",
          "They are usually larger than island mammals.",
          "They lose heat less easily than larger animals.",
          "They have no competition for food.",
        ],
        correct: 0,
      },
      {
        question: "Why does the author mention the size of the island and the length of isolation in paragraph 4?",
        options: [
          "To explain how dwarf elephants reached the Mediterranean",
          "To argue that gigantism is more common than dwarfism",
          "To describe how researchers measure fossils",
          "To show that the island rule depends on several variables",
        ],
        correct: 3,
      },
      {
        question: "Where would the following sentence best fit? \"Without enemies to avoid, there is little benefit in remaining small.\"",
        options: ["[A]", "[B]", "[C]", "[D]"],
        correct: 1,
      },
    ],
  },
];

// ── Easier Module 2 ──────────────────────────────────────────
const lower = [
  {
    id: "low-words",
    type: "words",
    title: "Penguins",
    text: `Penguins are birds that cannot fly. Instead, [[they]] are [[excellent]] [[swimmers]] [[and]] use [[their]] wings [[like]] flippers [[in]] the [[water]]. Most [[penguins]] live [[in]] the [[Southern]] Hemisphere, [[where]] the water is cold and full of fish. Their thick feathers and a layer of fat keep them warm.`,
  },
  {
    id: "low-daily1",
    type: "daily",
    title: "Outdoor Movie Night",
    text: `OUTDOOR MOVIE NIGHT
Friday, September 12 • 8:00 p.m.
Main Lawn (behind the Student Union)
Free popcorn for the first 100 guests!
Bring a blanket or a chair.
If it rains, the movie will be shown in the Student Union Theater.`,
    questions: [
      {
        question: "Where will the movie be shown if the weather is good?",
        options: ["In the Student Union Theater", "On the Main Lawn", "In the library", "At a local cinema"],
        correct: 1,
      },
      {
        question: "Who will get free popcorn?",
        options: ["Everyone", "Only students", "The first 100 guests", "People who bring a chair"],
        correct: 2,
      },
      {
        question: "What will happen if it rains?",
        options: [
          "The movie will move indoors.",
          "The movie will be canceled.",
          "The movie will start later.",
          "Guests will get free blankets.",
        ],
        correct: 0,
      },
    ],
  },
  {
    id: "low-daily2",
    type: "daily",
    title: "Bus Pass Reminder",
    text: `Reminder from City Transit: Your monthly bus pass will expire on October 31. You can buy a new pass online, at any ticket machine, or at the Central Station office. Students who show a valid student ID can buy a pass for $30 instead of the regular price of $55.`,
    questions: [
      {
        question: "What is the main purpose of the message?",
        options: [
          "To announce a new bus route",
          "To advertise student ID cards",
          "To explain the station's opening hours",
          "To remind the reader that a bus pass will expire",
        ],
        correct: 3,
      },
      {
        question: "How much does a monthly pass cost for students?",
        options: ["$30", "$55", "$25", "$85"],
        correct: 0,
      },
      {
        question: "What do students need to get the lower price?",
        options: ["A coupon", "A valid student ID", "An online account", "A letter from their school"],
        correct: 1,
      },
    ],
  },
  {
    id: "low-academic",
    type: "academic",
    title: "How Rainbows Form",
    text: `A rainbow is one of the most beautiful sights in nature. It appears as a curved band of colors in the sky, usually after a rain shower. The colors always appear in the same order: red, orange, yellow, green, blue, indigo, and violet.

Rainbows form when sunlight shines through drops of water in the air. [A] Sunlight looks white, but it is actually made of many colors mixed together. When light enters a raindrop, it bends. Each color bends by a slightly different amount, so the colors separate. [B]

The light then reflects off the back of the raindrop and leaves it again, bending a second time. [C] This sends the separated colors toward the person who is watching. To see a rainbow, you must stand with the sun behind you and the rain in front of you.

Sometimes people can see two rainbows at the same time. The second rainbow is lighter, and its colors are in the opposite order. [D] It happens when light reflects twice inside each raindrop before leaving it.`,
    questions: [
      {
        question: "The word \"separate\" in the passage is closest in meaning to",
        options: ["move apart", "mix", "disappear", "get brighter"],
        correct: 0,
      },
      {
        question: "According to paragraph 2, what is sunlight made of?",
        options: ["Only yellow light", "Water and air", "Many colors mixed together", "Heat from clouds"],
        correct: 2,
      },
      {
        question: "According to paragraph 3, where must the sun be for a person to see a rainbow?",
        options: ["Behind the person", "In front of the person", "Directly overhead", "Behind the clouds"],
        correct: 0,
      },
      {
        question: "Why does the author mention a second rainbow in paragraph 4?",
        options: [
          "To explain why rainbows are curved",
          "To show that rainbows appear only in the morning",
          "To compare rainbows with clouds",
          "To describe a less common kind of rainbow",
        ],
        correct: 3,
      },
      {
        question: "Where would the following sentence best fit? \"This is why a glass prism can also split light into a band of colors.\"",
        options: ["[A]", "[B]", "[C]", "[D]"],
        correct: 1,
      },
    ],
  },
];

export const readingModules = { module1, upper, lower };

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}
